// Federated sign-in UI (PB-17-04/05, ADR-011): provider buttons on the sign-in page and the page the
// identity provider returns to. The one-time result arrives in the URL fragment (never sent to a
// server log), is removed from the address bar, and is finished by the browser that started it.
import type { AuthContextResponse, MfaChallengeResponse, SessionResponse, SsoLinkRequiredResponse, SsoStartResponse } from '@passage/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiRequestError, api, setCsrfToken } from '../api.ts';
import { MfaEnrol, MfaVerifyStep, RecoveryCodes } from '../components/Mfa.tsx';
import { ErrorSummary, Field, Loading } from '../components/ui.tsx';
import { useI18n } from '../i18n.tsx';
import { SESSION_QUERY_KEY } from '../session.tsx';
import { AuthFrame } from './SignIn.tsx';

export function SsoButtons({ providers }: { providers: NonNullable<AuthContextResponse['ssoProviders']> }) {
  const { t } = useI18n();
  const m = useMutation({
    mutationFn: (id: string) => api<SsoStartResponse>('POST', `/api/v1/auth/sso/${id}/start`, {}),
    onSuccess: (r) => window.location.assign(r.redirectUrl),
  });
  return (
    <div className="stack">
      <ErrorSummary error={m.error} fieldIds={{}} />
      {providers.map((p) => (
        <button key={p.id} className="button dark" type="button" disabled={m.isPending} onClick={() => m.mutate(p.id)}>
          {m.isPending && m.variables === p.id ? t('auth.ssoRedirecting') : t('auth.ssoWith', { name: p.name })}
        </button>
      ))}
    </div>
  );
}

type Finish = SessionResponse | MfaChallengeResponse | SsoLinkRequiredResponse;

export function SsoFinishPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useState(() => new URLSearchParams(window.location.hash.replace(/^#/, '')));
  const result = params.get('r') ?? '';
  const [state, setState] = useState<{ kind: 'checking' } | { kind: 'failed' } | { kind: 'challenge'; c: MfaChallengeResponse } | { kind: 'link'; l: SsoLinkRequiredResponse } | { kind: 'codes'; codes: readonly string[]; s: SessionResponse }>(result ? { kind: 'checking' } : { kind: 'failed' });
  const [password, setPassword] = useState('');
  const started = useRef(false);

  function complete(s: SessionResponse) {
    setCsrfToken(s.csrfToken);
    qc.clear();
    qc.setQueryData(SESSION_QUERY_KEY, s);
    navigate('/', { replace: true });
  }
  const handle = (r: Finish) => {
    if ('next' in r) {
      if (r.next === 'link') setState({ kind: 'link', l: r });
      else setState({ kind: 'challenge', c: r });
    } else complete(r);
  };

  useEffect(() => {
    if (window.location.hash) history.replaceState(null, '', window.location.pathname);
    if (!result || started.current) return;
    started.current = true;
    api<Finish>('POST', '/api/v1/auth/sso/finish', { result }).then(handle, () => setState({ kind: 'failed' }));
  }, []);

  const link = useMutation({ mutationFn: () => api<Finish>('POST', '/api/v1/auth/sso/link', { result, password }), onSuccess: handle, onSettled: () => setPassword('') });

  return (
    <AuthFrame>
      <div className="auth-card">
        {state.kind === 'checking' ? (
          <>
            <h1>{t('sso.title')}</h1>
            <Loading label={t('sso.checking')} />
          </>
        ) : state.kind === 'failed' ? (
          <>
            <h1>{t('sso.title')}</h1>
            <div className="note danger" role="alert" style={{ marginBlockStart: 24 }}>{t('sso.failed')}</div>
            <Link className="button" to="/sign-in">{t('sso.back')}</Link>
          </>
        ) : state.kind === 'link' ? (
          <>
            <h1>{t('sso.link.title')}</h1>
            <p className="lede">{t('sso.link.lede', { provider: state.l.providerName, email: state.l.email })}</p>
            <form noValidate onSubmit={(e: FormEvent) => { e.preventDefault(); link.mutate(); }}>
              {link.error ? <div className="note danger" role="alert">{link.error instanceof ApiRequestError ? link.error.message : t('error.requestFailed')}</div> : null}
              <Field label={t('auth.password')}>
                {(p) => <input {...p} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />}
              </Field>
              <button className="button dark" type="submit" disabled={link.isPending || !password}>{link.isPending ? t('auth.submitting') : t('sso.link.submit')}</button>
              <Link className="button" to="/sign-in">{t('sso.back')}</Link>
            </form>
          </>
        ) : state.kind === 'codes' ? (
          <RecoveryCodes codes={state.codes} onDone={() => complete(state.s)} />
        ) : (
          <>
            <h1>{state.c.next === 'mfa' ? t('mfa.title') : t('mfa.enrol.title')}</h1>
            {state.c.next === 'mfa' ? (
              <MfaVerifyStep<SessionResponse> base="/api/v1" challengeToken={state.c.challengeToken} onDone={complete} />
            ) : (
              <MfaEnrol<SessionResponse & { recoveryCodes: string[] }>
                base="/api/v1"
                challengeToken={state.c.challengeToken}
                required
                onDone={(r) => {
                  const { recoveryCodes, ...s } = r;
                  setState({ kind: 'codes', codes: recoveryCodes, s: s as SessionResponse });
                }}
              />
            )}
          </>
        )}
      </div>
    </AuthFrame>
  );
}
